import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart3, Users, Ticket, Star, Image, Film, Download, ArrowLeft, TrendingUp, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5 } }) };

function StatCard({ icon: Icon, label, value, sub, custom }) {
  return (
    <motion.div variants={fadeUp} custom={custom} className="glass-card p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-white/30 text-[10px] font-body uppercase tracking-wider">{label}</span>
        <Icon size={16} className="text-gold/40" />
      </div>
      <p className="text-white font-heading text-2xl" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
      {sub && <p className="text-white/30 text-xs font-body mt-1">{sub}</p>}
    </motion.div>
  );
}

function RatingBar({ label, value, max = 5 }) {
  const pct = (value / max) * 100;
  return (
    <div className="flex items-center gap-3">
      <span className="text-white/40 text-xs font-body w-24 text-right">{label}</span>
      <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full bg-gold rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-white/60 text-xs font-body w-10">{value}/5</span>
    </div>
  );
}

export default function EventAnalyticsPage() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/events/${eventId}/analytics`).then(r => setData(r.data)).catch(() => { toast.error("Failed to load analytics."); }).finally(() => setLoading(false));
  }, [eventId]);

  const downloadPdf = () => {
    const link = document.createElement("a");
    link.href = `${API}/admin/events/${eventId}/analytics/pdf`;
    const token = localStorage.getItem("auth_token");
    if (token) {
      fetch(`${API}/admin/events/${eventId}/analytics/pdf`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.blob())
        .then(blob => { const url = URL.createObjectURL(blob); link.href = url; link.download = `report-${eventId}.pdf`; link.click(); URL.revokeObjectURL(url); })
        .catch(() => toast.error("Failed to download PDF."));
    }
  };

  if (loading) return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><div className="text-gold animate-pulse font-body">Loading analytics...</div></div>;
  if (!data) return null;

  const { event, attendance, tickets, reviews, media } = data;

  return (
    <div data-testid="event-analytics-page" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/30 hover:text-gold text-xs font-body mb-4 transition-colors"><ArrowLeft size={14} /> Back to Dashboard</Link>
            <h1 className="font-heading text-3xl sm:text-4xl text-white" data-testid="analytics-event-title">{event.title}</h1>
            <p className="text-white/40 font-body text-sm mt-1">{event.event_type} &middot; {event.event_date} &middot; {event.location}</p>
          </div>
          <button onClick={downloadPdf} data-testid="download-pdf-btn" className="flex items-center gap-2 bg-gold/15 text-gold px-5 py-3 text-xs font-body hover:bg-gold/25 transition-colors">
            <Download size={14} /> Download PDF Report
          </button>
        </div>

        <motion.div initial="hidden" animate="visible" className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
          <StatCard icon={Users} label="Check-ins" value={attendance.total_checkins} sub={`${attendance.checkin_via_ticket} via ticket`} custom={0} />
          <StatCard icon={Ticket} label="Tickets Sold" value={tickets.total_sold} sub={`$${tickets.revenue.toLocaleString()} revenue`} custom={1} />
          <StatCard icon={Star} label="Reviews" value={reviews.total} sub={`${reviews.avg_ratings.overall}/5 avg`} custom={2} />
          <StatCard icon={Image} label="Media" value={media.total} sub={`${media.photos} photos, ${media.videos} videos`} custom={3} />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          {/* Ticket Breakdown */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={4} className="glass-card p-8" data-testid="ticket-breakdown">
            <h2 className="font-heading text-lg text-white mb-6 flex items-center gap-2"><TrendingUp size={18} className="text-gold" /> Ticket Revenue Breakdown</h2>
            {Object.keys(tickets.breakdown).length === 0 ? <p className="text-white/30 text-sm font-body">No ticket sales data yet.</p> : (
              <div className="space-y-4">
                {Object.entries(tickets.breakdown).map(([name, info]) => (
                  <div key={name} className="flex items-center justify-between pb-3 border-b border-white/5">
                    <div>
                      <p className="text-white/80 font-body text-sm">{name}</p>
                      <p className="text-white/30 font-body text-xs">{info.count} tickets</p>
                    </div>
                    <p className="text-gold font-body font-medium">${info.revenue.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Review Ratings */}
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={5} className="glass-card p-8" data-testid="review-ratings">
            <h2 className="font-heading text-lg text-white mb-6 flex items-center gap-2"><Star size={18} className="text-gold" /> Guest Ratings</h2>
            {reviews.total === 0 ? <p className="text-white/30 text-sm font-body">No reviews yet.</p> : (
              <div className="space-y-4">
                <RatingBar label="Overall" value={reviews.avg_ratings.overall} />
                <RatingBar label="Ticketing" value={reviews.avg_ratings.ticketing} />
                <RatingBar label="Ambiance" value={reviews.avg_ratings.ambiance} />
                <RatingBar label="Food & Drink" value={reviews.avg_ratings.food_drink} />
              </div>
            )}
          </motion.div>
        </div>

        {/* Feedback */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={6} className="glass-card p-8" data-testid="liked-feedback">
            <h2 className="font-heading text-lg text-white mb-4 flex items-center gap-2"><MessageCircle size={18} className="text-green-400" /> What Guests Loved</h2>
            {reviews.liked_highlights.length === 0 ? <p className="text-white/30 text-sm font-body">No feedback yet.</p> : (
              <div className="space-y-3">
                {reviews.liked_highlights.map((f, i) => (
                  <p key={i} className="text-white/60 font-body text-sm pl-4 border-l-2 border-green-400/30">{f}</p>
                ))}
              </div>
            )}
          </motion.div>
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={7} className="glass-card p-8" data-testid="improve-feedback">
            <h2 className="font-heading text-lg text-white mb-4 flex items-center gap-2"><MessageCircle size={18} className="text-orange-400" /> Areas to Improve</h2>
            {reviews.improve_highlights.length === 0 ? <p className="text-white/30 text-sm font-body">No improvement feedback yet.</p> : (
              <div className="space-y-3">
                {reviews.improve_highlights.map((f, i) => (
                  <p key={i} className="text-white/60 font-body text-sm pl-4 border-l-2 border-orange-400/30">{f}</p>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
